import { useLocation } from 'react-router-dom';
import type { Sector } from '@chicare/shared';

import type { IconName } from '@/components/icons/iconPaths';

/**
 * Which end of ChiCare you are standing in.
 *
 * ── WHY THE URL DECIDES, AND NOT A TOGGLE IN STATE ──────────────────────────
 *
 * A stored "I am browsing animal health" flag is a flag that can be stale. Come
 * back to a tab an hour later, follow a link from WhatsApp, share a URL, press
 * Back — every one of those is a chance for the chrome to say one thing while
 * the results say another, and on this app that means somebody reading cattle
 * dosing under a heading that says medicines.
 *
 * The path IS the sector. /vet and everything under it is animal health;
 * everything else is human. There is no third state and nothing to synchronise.
 *
 * ── WHY THE CART AND THE ACCOUNT ARE NOT SPLIT ──────────────────────────────
 *
 * A farmer with a dog and a headache is one person with one account, one
 * address book and one order history. Only BROWSING is walled: the shelf, the
 * search and the product page. Everything downstream of "add to cart" is
 * shared, and each line says which side it came from.
 */

/**
 * ── WHAT THE TWO SIDES ARE CALLED ───────────────────────────────────────────
 *
 * "Pharmacy" and "Veterinary", not "Human medicines" and "Animal health".
 *
 * The long descriptive pairs were accurate and too heavy: they are three and
 * two words wide, which made the switch that carries them the biggest thing on
 * the home screen — louder than the search box, and louder than the shelf it is
 * only meant to label. One word each reads faster, fits a phone, and is what a
 * Nigerian buyer would say out loud anyway.
 *
 * The longer wording survives as `blurb`, where there is room for it: the door
 * above the footer, which has to explain rather than label.
 */
export const SECTORS = {
  human: {
    key: 'human' as const,
    /** The wordmark suffix. Empty on the human side: ChiCare is the default. */
    wordmark: '',
    label: 'Pharmacy',
    short: 'Pharmacy',
    icon: 'pill' as const,
    blurb: 'Medicines, equipment and wellness for people.',
    home: '/',
    /** Where the OTHER end lives, for the door between them. */
    otherHome: '/vet',
    otherLabel: 'Veterinary',
    otherBlurb: 'Veterinary medicines and equipment, for livestock and pets.',
    searchPlaceholder: 'Search medicines, conditions, brands',
  },
  veterinary: {
    key: 'veterinary' as const,
    wordmark: 'Vet',
    label: 'Veterinary',
    short: 'Veterinary',
    icon: 'paw' as const,
    blurb: 'Veterinary medicines and equipment, for livestock and pets.',
    home: '/vet',
    otherHome: '/',
    otherLabel: 'Pharmacy',
    otherBlurb: 'Medicines, equipment and wellness for people.',
    searchPlaceholder: 'Search by animal, condition or product',
  },
} satisfies Record<Sector, SectorInfo>;

interface SectorInfo {
  key: Sector;
  wordmark: string;
  label: string;
  short: string;
  /** The mark beside the name on the switch. */
  icon: IconName;
  blurb: string;
  home: string;
  otherHome: string;
  otherLabel: string;
  otherBlurb: string;
  searchPlaceholder: string;
}

/**
 * The line printed across the top of every screen in a sector.
 *
 * Blunt on the veterinary side on purpose. "Animal health" is a category name;
 * "nothing here is for a person" is an instruction, and the instruction is what
 * stops somebody dosing a child with a product bought for a goat.
 */
export const VET_STANDING = 'For animals only — nothing here is for human use.';

export function sectorFromPath(pathname: string): Sector {
  return pathname === '/vet' || pathname.startsWith('/vet/') ? 'veterinary' : 'human';
}

export function useSector(): Sector {
  return sectorFromPath(useLocation().pathname);
}

export function useSectorInfo(): SectorInfo {
  return SECTORS[useSector()];
}

/**
 * The shelves on each side, as the navigation lists them.
 *
 * Two literal lists rather than one filtered at render time: the human header
 * cannot leak a veterinary slug through a stale filter, because it does not
 * hold one. They live here rather than in a layout because the desktop header,
 * the mobile switcher and anything else that offers a way in all have to agree
 * — a category that exists in one bar and not the other is how somebody
 * concludes the vet end has nothing in it.
 */
export const SECTOR_CATEGORIES = {
  human: [
    ['Medicines', 'medicines'],
    ['Equipment', 'equipment'],
    ['Wellness', 'wellness'],
    ['Baby & mother', 'baby-mother'],
    ['First aid', 'first-aid'],
    ['Chronic care', 'chronic-care'],
  ],
  veterinary: [
    ['Veterinary medicines', 'vet-medicines'],
    ['Dewormers', 'vet-antiparasitics'],
    ['Vaccines', 'vet-vaccines'],
    ['Poultry health', 'vet-poultry'],
    ['Pet care', 'vet-pet-care'],
    ['Veterinary equipment', 'vet-equipment'],
  ],
} satisfies Record<Sector, ReadonlyArray<readonly [string, string]>>;

/**
 * Build a path that stays on this side of the wall.
 *
 *   sectorPath('human', '/search')       →  /search
 *   sectorPath('veterinary', '/search')  →  /vet/search
 *
 * Every in-sector link goes through this. A hard-coded `/search` on a
 * veterinary screen is how somebody ends up back among human medicines holding
 * a search they typed for their poultry.
 */
export function sectorPath(sector: Sector, path: string): string {
  const suffix = path === '/' ? '' : path;

  return sector === 'veterinary' ? `/vet${suffix}` : suffix || '/';
}

export function useSectorPath(): (path: string) => string {
  const sector = useSector();

  return (path: string) => sectorPath(sector, path);
}

/**
 * The paths that genuinely exist on BOTH sides of the wall.
 *
 * Taken from the router in App.tsx, where the veterinary shelf has its own
 * home, search, product pages, blog and prescription board — and nothing else.
 * There is no `/vet/support`, no `/vet/cart`, no `/vet/verify`.
 */
const TWINNED: ReadonlyArray<(route: string) => boolean> = [
  (route) => route === '/',
  (route) => route === '/search',
  (route) => route.startsWith('/products/'),
  (route) => route === '/blog' || route.startsWith('/blog/'),
  // Exact, and not the branch. `/vet/prescriptions` is the veterinary board;
  // `/vet/prescriptions/CHI-123` is not a route, because one prescription's
  // page is not duplicated per shelf.
  (route) => route === '/prescriptions',
];

/**
 * A link an ADMIN chose, placed on whichever shelf it is being drawn on.
 *
 * ────────────────── WHY THIS IS NOT JUST sectorPath ──────────────────
 *
 * `sectorPath` prefixes unconditionally, which is correct everywhere it is
 * used today: every call site passes a path the author knows has a veterinary
 * twin. A shop-front tile does not have that luxury — the path comes out of the
 * database, chosen by somebody in an admin console, and it may be `/support`.
 *
 * `sectorPath('veterinary', '/support')` is `/vet/support`, which matches no
 * route and renders the not-found screen. The tile would look saved, the link
 * would look right in the console, and only a buyer on the animal-health shelf
 * would ever find out. So the prefix is applied to the paths that have a twin
 * and withheld from the ones that do not.
 *
 * The human shelf is unaffected either way: nothing is prefixed there.
 */
export function sectorLink(sector: Sector, path: string): string {
  if (sector !== 'veterinary') {
    return path;
  }

  /**
   * The prefix is stripped before the decision, not trusted to be absent.
   *
   * An admin standing on the animal-health shelf copies a URL out of the
   * address bar and stores `/vet/search?category=vet-vaccines`. Prefixing that
   * again gives `/vet/vet/search?…` — which looked right in the console, read
   * right in the database, and was how four of the six seeded banner slides
   * came to lead nowhere. It cannot happen now.
   */
  const bare = path === '/vet' ? '/' : path.startsWith('/vet/') ? path.slice(4) : path;

  // Compared without the query string. `/search?category=x` is twinned
  // because `/search` is the route; the query is not part of the decision.
  const route = bare.split(/[?#]/)[0] || bare;

  if (!TWINNED.some((twin) => twin(route))) {
    return bare;
  }

  // `/vet`, not `/vet/`. Same reason `sectorPath` special-cases it: a trailing
  // slash is a different string, and nothing guarantees the router forgives it.
  return bare === '/' ? '/vet' : `/vet${bare}`;
}

export function useSectorLink(): (path: string) => string {
  const sector = useSector();

  return (path: string) => sectorLink(sector, path);
}

/**
 * Where a product's page lives.
 *
 * Derived from the PRODUCT and not from wherever the link happens to be
 * rendered, so a veterinary card in a cart line, a shared screenshot or an
 * order history row all land on the veterinary product page — with its standing
 * strip and its withdrawal periods — rather than opening dressed as a human
 * medicine and redirecting a beat later.
 */
export function productPath(product: { slug: string; is_veterinary?: boolean }): string {
  return product.is_veterinary
    ? `/vet/products/${product.slug}`
    : `/products/${product.slug}`;
}

/**
 * The sector to send to the API.
 *
 * Human is the server's default, so it is omitted rather than sent — which
 * keeps the human URLs identical to what they were before animal health
 * existed, and keeps every cached response keyed the way it already was.
 */
export function sectorParam(sector: Sector): 'veterinary' | undefined {
  return sector === 'veterinary' ? 'veterinary' : undefined;
}
